(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-buddy-island]';
  const LOTTIE_SELECTOR = '[data-bb-lottie]';
  const CAROUSEL_SELECTOR = '[data-bb-carousel]';
  const SLIDE_SELECTOR = '[data-bb-slide]';
  const INFO_SELECTOR = '[data-bb-product-info]';
  const NAV_SELECTOR = '[data-bb-carousel-nav]';
  const DOT_SELECTOR = '[data-bb-carousel-dot]';

  const LOTTIE_LIBRARY_URL =
    'https://cdnjs.cloudflare.com/ajax/libs/bodymovin/5.12.2/lottie.min.js';

  const AUTOPLAY_DELAY = 8000;
  const TRANSITION_DURATION = 700;
  const MINIMUM_SWIPE_DISTANCE = 50;

  let lottieLibraryPromise = null;

  function loadLottieLibrary() {
    if (window.lottie) {
      return Promise.resolve(window.lottie);
    }

    if (lottieLibraryPromise) {
      return lottieLibraryPromise;
    }

    lottieLibraryPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector(
        'script[data-bb-lottie-library]'
      );

      if (existingScript) {
        existingScript.addEventListener(
          'load',
          () => resolve(window.lottie),
          { once: true }
        );
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.src = LOTTIE_LIBRARY_URL;
      script.defer = true;
      script.dataset.bbLottieLibrary = 'true';

      script.addEventListener(
        'load',
        () => resolve(window.lottie),
        { once: true }
      );
      script.addEventListener('error', reject, { once: true });

      document.head.appendChild(script);
    });

    return lottieLibraryPromise;
  }

  class BuddyIslandCarousel {
    constructor(section) {
      this.section = section;
      this.carousel = section.querySelector(CAROUSEL_SELECTOR);
      this.slides = this.carousel
        ? Array.from(this.carousel.querySelectorAll(SLIDE_SELECTOR))
        : [];

      this.productInfos = Array.from(
        section.querySelectorAll(INFO_SELECTOR)
      );

      this.navigation = section.querySelector(NAV_SELECTOR);
      this.dots = this.navigation
        ? Array.from(this.navigation.querySelectorAll(DOT_SELECTOR))
        : [];

      this.treeContainers = Array.from(
        section.querySelectorAll(LOTTIE_SELECTOR)
      );
      this.treeAnimations = [];

      this.currentIndex = 0;
      this.timer = null;
      this.transitionTimer = null;

      this.isPaused = false;
      this.isTransitioning = false;
      this.isInitialized = false;

      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.didSwipe = false;

      this.handlePointerEnter = this.pause.bind(this);
      this.handlePointerLeave = this.play.bind(this);
      this.handleFocusIn = this.pause.bind(this);
      this.handleFocusOut = this.handleSectionFocusOut.bind(this);
      this.handleNavigationClick =
        this.handleNavigationClick.bind(this);
      this.handleTouchStart = this.handleTouchStart.bind(this);
      this.handleTouchMove = this.handleTouchMove.bind(this);
      this.handleTouchEnd = this.handleTouchEnd.bind(this);
      this.handleCarouselClick = this.handleCarouselClick.bind(this);
    }

    async initialize() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.initializeSlides();
      this.bindEvents();

      await this.initializeTrees();
      this.play();
    }

    async initializeTrees() {
      if (!this.treeContainers.length) return;

      try {
        const lottie = await loadLottieLibrary();

        if (!lottie) {
          console.warn("Buddy's Island: Lottie did not load.");
          return;
        }

        this.treeContainers.forEach((container) => {
          if (container.bbAnimation) {
            this.treeAnimations.push(container.bbAnimation);
            return;
          }

          const animationUrl = container.dataset.animationUrl;
          if (!animationUrl) return;

          const animation = lottie.loadAnimation({
            container,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            path: animationUrl,
            rendererSettings: {
              preserveAspectRatio: 'xMidYMid meet',
              progressiveLoad: true
            }
          });

          container.bbAnimation = animation;
          this.treeAnimations.push(animation);
        });
      } catch (error) {
        console.error(
          "Buddy's Island: Unable to load Lottie.",
          error
        );
      }
    }

    initializeSlides() {
      if (!this.slides.length) return;

      this.currentIndex = Math.floor(
        Math.random() * this.slides.length
      );

      this.slides.forEach((slide, index) => {
        const isActive = index === this.currentIndex;

        slide.hidden = !isActive;
        slide.classList.toggle('is-active', isActive);
        slide.classList.remove('is-entering', 'is-leaving');
        slide.setAttribute('aria-hidden', String(!isActive));

        if (isActive) {
          slide.removeAttribute('tabindex');
        } else {
          slide.setAttribute('tabindex', '-1');
        }
      });

      this.updateInterface(this.currentIndex);
    }

    updateInterface(activeIndex) {
      this.dots.forEach((button, index) => {
        const isActive = index === activeIndex;

        button.classList.toggle('is-active', isActive);
        button.setAttribute(
          'aria-current',
          isActive ? 'true' : 'false'
        );
      });

      this.productInfos.forEach((info, index) => {
        const isActive = index === activeIndex;

        info.hidden = !isActive;
        info.classList.toggle('is-active', isActive);
        info.setAttribute('aria-hidden', String(!isActive));
      });
    }

    bindEvents() {
      this.section.addEventListener(
        'mouseenter',
        this.handlePointerEnter
      );
      this.section.addEventListener(
        'mouseleave',
        this.handlePointerLeave
      );
      this.section.addEventListener('focusin', this.handleFocusIn);
      this.section.addEventListener('focusout', this.handleFocusOut);

      if (this.navigation) {
        this.navigation.addEventListener(
          'click',
          this.handleNavigationClick
        );
      }

      if (this.carousel) {
        this.carousel.addEventListener(
          'touchstart',
          this.handleTouchStart,
          { passive: true }
        );
        this.carousel.addEventListener(
          'touchmove',
          this.handleTouchMove,
          { passive: true }
        );
        this.carousel.addEventListener(
          'touchend',
          this.handleTouchEnd,
          { passive: true }
        );
        this.carousel.addEventListener(
          'touchcancel',
          this.handleTouchEnd,
          { passive: true }
        );
        this.carousel.addEventListener(
          'click',
          this.handleCarouselClick,
          true
        );
      }
    }

    handleNavigationClick(event) {
      const button = event.target.closest(DOT_SELECTOR);

      if (!button || !this.navigation?.contains(button)) return;

      const index = Number(button.dataset.slideIndex);
      if (!Number.isInteger(index)) return;

      this.goTo(index);

      if (!this.isPaused) {
        this.startTimer();
      }
    }

    handleTouchStart(event) {
      const touch = event.touches[0];
      if (!touch) return;

      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.didSwipe = false;

      this.stopTimer();
    }

    handleTouchMove(event) {
      const touch = event.touches[0];
      if (!touch) return;

      this.touchDeltaX = touch.clientX - this.touchStartX;
      this.touchDeltaY = touch.clientY - this.touchStartY;
    }

    handleTouchEnd() {
      const horizontalDistance = Math.abs(this.touchDeltaX);
      const verticalDistance = Math.abs(this.touchDeltaY);

      const isHorizontalSwipe =
        horizontalDistance >= MINIMUM_SWIPE_DISTANCE &&
        horizontalDistance > verticalDistance;

      if (isHorizontalSwipe) {
        this.didSwipe = true;

        if (this.touchDeltaX < 0) {
          this.next();
        } else {
          this.previous();
        }
      }

      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;

      if (!this.isPaused) {
        this.startTimer();
      }
    }

    handleCarouselClick(event) {
      if (!this.didSwipe) return;

      event.preventDefault();
      event.stopPropagation();
      this.didSwipe = false;
    }

    next() {
      if (this.slides.length <= 1) return;

      const nextIndex =
        (this.currentIndex + 1) % this.slides.length;

      this.goTo(nextIndex);
    }

    previous() {
      if (this.slides.length <= 1) return;

      const previousIndex =
        (this.currentIndex - 1 + this.slides.length) %
        this.slides.length;

      this.goTo(previousIndex);
    }

    goTo(index) {
      if (!this.slides.length) return;
      if (this.isTransitioning) return;
      if (index < 0 || index >= this.slides.length) return;
      if (index === this.currentIndex) return;

      const currentSlide = this.slides[this.currentIndex];
      const nextSlide = this.slides[index];

      if (!currentSlide || !nextSlide) return;

      this.isTransitioning = true;
      this.currentIndex = index;
      this.updateInterface(index);

      window.clearTimeout(this.transitionTimer);

      nextSlide.hidden = false;
      nextSlide.classList.remove('is-active', 'is-leaving');
      nextSlide.classList.add('is-entering');
      nextSlide.setAttribute('aria-hidden', 'false');
      nextSlide.setAttribute('tabindex', '-1');

      currentSlide.classList.remove('is-entering');
      currentSlide.classList.add('is-leaving');
      currentSlide.setAttribute('aria-hidden', 'true');
      currentSlide.setAttribute('tabindex', '-1');

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          nextSlide.classList.remove('is-entering');
          nextSlide.classList.add('is-active');
        });
      });

      this.transitionTimer = window.setTimeout(() => {
        currentSlide.hidden = true;
        currentSlide.classList.remove(
          'is-active',
          'is-entering',
          'is-leaving'
        );

        nextSlide.hidden = false;
        nextSlide.classList.remove('is-entering', 'is-leaving');
        nextSlide.classList.add('is-active');
        nextSlide.setAttribute('aria-hidden', 'false');
        nextSlide.removeAttribute('tabindex');

        this.isTransitioning = false;
        this.transitionTimer = null;
      }, TRANSITION_DURATION);
    }

pause() {
  this.isPaused = true;

  // Pause only the shirt carousel autoplay.
  // Keep the palm tree Lotties running.
  this.stopTimer();
}

    play() {
      this.isPaused = false;

      this.treeAnimations.forEach((animation) => {
        animation.play();
      });

      this.startTimer();
    }

    startTimer() {
      this.stopTimer();

      if (this.isPaused || this.slides.length <= 1) return;

      this.timer = window.setInterval(() => {
        this.next();
      }, AUTOPLAY_DELAY);
    }

    stopTimer() {
      if (!this.timer) return;

      window.clearInterval(this.timer);
      this.timer = null;
    }

    handleSectionFocusOut(event) {
      const nextFocusedElement = event.relatedTarget;

      if (
        nextFocusedElement &&
        this.section.contains(nextFocusedElement)
      ) {
        return;
      }

      this.play();
    }

    destroy() {
      this.stopTimer();
      window.clearTimeout(this.transitionTimer);
      this.transitionTimer = null;

      this.section.removeEventListener(
        'mouseenter',
        this.handlePointerEnter
      );
      this.section.removeEventListener(
        'mouseleave',
        this.handlePointerLeave
      );
      this.section.removeEventListener(
        'focusin',
        this.handleFocusIn
      );
      this.section.removeEventListener(
        'focusout',
        this.handleFocusOut
      );

      if (this.navigation) {
        this.navigation.removeEventListener(
          'click',
          this.handleNavigationClick
        );
      }

      if (this.carousel) {
        this.carousel.removeEventListener(
          'touchstart',
          this.handleTouchStart
        );
        this.carousel.removeEventListener(
          'touchmove',
          this.handleTouchMove
        );
        this.carousel.removeEventListener(
          'touchend',
          this.handleTouchEnd
        );
        this.carousel.removeEventListener(
          'touchcancel',
          this.handleTouchEnd
        );
        this.carousel.removeEventListener(
          'click',
          this.handleCarouselClick,
          true
        );
      }

      this.treeAnimations.forEach((animation) => {
        animation.destroy();
      });

      this.treeContainers.forEach((container) => {
        delete container.bbAnimation;
        container.innerHTML = '';
      });

      this.treeAnimations = [];
      this.isInitialized = false;
    }
  }

  function initializeSection(section) {
    if (!section || section.bbIslandController) return;

    const controller = new BuddyIslandCarousel(section);

    section.bbIslandController = controller;
    controller.initialize();
  }

  function initializeAllSections(root = document) {
    if (root.matches?.(SECTION_SELECTOR)) {
      initializeSection(root);
    }

    root.querySelectorAll?.(SECTION_SELECTOR).forEach((section) => {
      initializeSection(section);
    });
  }

  function destroySection(section) {
    if (!section?.bbIslandController) return;

    section.bbIslandController.destroy();
    delete section.bbIslandController;
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeAllSections(),
      { once: true }
    );
  } else {
    initializeAllSections();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAllSections(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.matches?.(SECTION_SELECTOR)
      ? event.target
      : event.target.querySelector?.(SECTION_SELECTOR);

    destroySection(section);
  });
})();